<template>
  <div class="time-filter-root" v-if="filterSelected && filterOptions">
    <div class="time-filter-container" :class="{ 'time-filter-rtl': isRTL }">
      <template v-if="fields.dateRange">
        <Select
          v-if="$route.name === 'overview'"
          :value="'last_month'"
          :disabled="true"
          class="filter-select"
        />

        <button
          v-else
          type="button"
          class="time-filter-trigger"
          :class="{ 'time-filter-trigger-active': dialogOpen }"
          :aria-haspopup="'dialog'"
          :aria-expanded="dialogOpen ? 'true' : 'false'"
          @click="openDialog"
        >
          <svg class="time-filter-trigger-icon" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 9h18M8 3v4M16 3v4" />
          </svg>
          <span class="time-filter-trigger-label">{{ triggerLabel }}</span>
          <svg class="time-filter-trigger-chevron" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </template>

      <Select
        v-if="fields.viewBy"
        @input="handleViewBySelect"
        :disabled="$route.name === 'overview'"
        :value="filterSelected.viewTimeBy"
        class="filter-select view-by-select"
      >
        <Option
          v-for="item in filterOptions.viewBy"
          :key="item.label"
          :label="$t(item.label)"
          :value="item.value"
        />
      </Select>
    </div>

    <Teleport
      v-if="dialogOpen && dialogTeleportTarget"
      :to="dialogTeleportTarget"
    >
      <div
        class="date-dialog-backdrop"
        @click.self="closeDialog"
        @keydown.esc="closeDialog"
        tabindex="-1"
      >
        <section
          class="date-dialog"
          :class="{ 'date-dialog-rtl': isRTL }"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('time_range') || 'Time range'"
        >
          <header class="date-dialog-header">
            <div class="date-dialog-title">
              <h3>{{ $t('time_range') || 'Time range' }}</h3>
            </div>
            <button
              type="button"
              class="date-dialog-close"
              :aria-label="$t('close') || 'Close'"
              @click="closeDialog"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div class="date-dialog-body">
            <ul class="time-presets" role="listbox">
              <li v-for="type in presetTypes" :key="type">
                <button
                  type="button"
                  class="time-preset"
                  :class="{ 'time-preset-active': draftTimeType === type }"
                  role="option"
                  :aria-selected="draftTimeType === type ? 'true' : 'false'"
                  @click="selectPreset(type)"
                >
                  <span class="time-preset-label">{{ $t(type) }}</span>
                  <svg
                    v-if="draftTimeType === type"
                    class="time-preset-check"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  class="time-preset time-preset-custom"
                  :class="{ 'time-preset-active': draftTimeType === 'custom_time' }"
                  role="option"
                  :aria-selected="draftTimeType === 'custom_time' ? 'true' : 'false'"
                  @click="selectCustom"
                >
                  <span class="time-preset-label">{{ $t('custom_time') }}</span>
                  <svg
                    v-if="draftTimeType === 'custom_time'"
                    class="time-preset-check"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </button>
              </li>
            </ul>

            <div v-if="draftTimeType === 'custom_time'" class="date-dialog-fields">
              <div class="date-field-card">
                <label class="date-field-label" for="time-filter-from">{{ $t('start') }}</label>
                <DatePicker
                  id="time-filter-from"
                  class="date-picker-dialog"
                  :value="draftRange.timeFrom"
                  :clearable="false"
                  :disabled-dates="isDateOutsideStatsBounds"
                  @input="handleDateInput($event, 'timeFrom')"
                />
              </div>
              <div class="date-field-card">
                <label class="date-field-label" for="time-filter-to">{{ $t('end') }}</label>
                <DatePicker
                  id="time-filter-to"
                  class="date-picker-dialog"
                  :value="draftRange.timeTo"
                  :clearable="false"
                  :disabled-dates="isDateOutsideStatsBounds"
                  @input="handleDateInput($event, 'timeTo')"
                />
              </div>
            </div>
          </div>

          <footer
            v-if="draftTimeType === 'custom_time'"
            class="date-dialog-footer"
          >
            <button type="button" class="date-dialog-cancel" @click="closeDialog">
              {{ $t('cancel') }}
            </button>
            <button
              type="button"
              class="date-dialog-apply"
              :disabled="!canApply"
              @click="apply"
            >
              {{ $t('filter') }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script>
import Select from '../Select';
import Option from '../Option';
import DatePicker from '../DatePicker';
import { mapGetters } from 'vuex';
import { Toast } from '@/modules/common/Toast';
import utilService from '@/modules/common/services/utilService';
import { getFirstStatsDate } from '@/modules/common/services/filterService';
import Teleport from 'vue2-teleport';

function toLocalYmd(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function startOfLocalDay(dateOrYmd) {
  if (typeof dateOrYmd === 'string') {
    const [y, m, d] = dateOrYmd.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }
  return new Date(dateOrYmd.getFullYear(), dateOrYmd.getMonth(), dateOrYmd.getDate(), 0, 0, 0, 0);
}

export default {
  data() {
    return {
      dialogOpen: false,
      dialogTeleportTarget: null,
      originalBodyOverflow: null,
      draftTimeType: '',
      draftRange: {
        timeFrom: '',
        timeTo: '',
      },
      /** @type {string|null} YYYY-MM-DD */
      firstStatsDate: null,
      firstStatsDateSiteId: null,
    };
  },
  watch: {
    dialogOpen(isOpen) {
      if (isOpen) {
        this.originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this.originalBodyOverflow || '';
        this.originalBodyOverflow = null;
      }
    },
    'filterSelected.site': {
      handler() {
        this.firstStatsDate = null;
        this.firstStatsDateSiteId = null;
        if (this.dialogOpen) this.loadFirstStatsDate();
      },
    },
  },
  mounted() {
    this.dialogTeleportTarget = document.querySelector('.main') ? '.main' : 'body';
  },
  beforeDestroy() {
    if (this.dialogOpen) {
      document.body.style.overflow = this.originalBodyOverflow || '';
    }
  },
  methods: {
    async loadFirstStatsDate() {
      const siteId = this.filterSelected?.site;
      if (!siteId) {
        this.firstStatsDate = null;
        this.firstStatsDateSiteId = null;
        return;
      }
      if (String(this.firstStatsDateSiteId) === String(siteId)) {
        return;
      }
      this.firstStatsDateSiteId = siteId;
      this.firstStatsDate = await getFirstStatsDate(siteId);
    },
    /**
     * Disable days before first stats day and after today.
     * @param {Date} date
     * @returns {boolean}
     */
    isDateOutsideStatsBounds(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return true;
      const day = startOfLocalDay(date);
      const today = startOfLocalDay(new Date());
      if (day > today) return true;
      if (this.firstStatsDate) {
        const first = startOfLocalDay(this.firstStatsDate);
        if (day < first) return true;
      }
      return false;
    },
    async openDialog() {
      this.draftTimeType = this.filterSelected.timeType || '';
      this.draftRange = {
        timeFrom: this.filterSelected.time?.timeFrom || '',
        timeTo: this.filterSelected.time?.timeTo || '',
      };
      this.dialogOpen = true;
      await this.loadFirstStatsDate();
      this.$nextTick(() => {
        const el = document.querySelector('.date-dialog-backdrop');
        if (el) el.focus();
      });
    },
    closeDialog() {
      this.dialogOpen = false;
    },
    selectPreset(type) {
      if (this.isLoading) {
        new Toast({
          message: this.$t('data_loading_please_wait') || 'Data is currently loading. Please wait...',
          type: 'warning',
          duration: 3000,
        });
        return;
      }
      this.draftTimeType = type;
      if (this.filterSelected.timeType !== type) {
        this.$store.commit({ type: 'updateFilter', field: 'timeType', value: type });
        this.$emit('input', 'timeType', type);
      }
      this.dialogOpen = false;
    },
    selectCustom() {
      this.draftTimeType = 'custom_time';
    },
    handleViewBySelect(value) {
      this.$emit('input', 'viewTimeBy', value);
    },
    handleDateInput(value, field) {
      const currentValue =
        field === 'timeFrom'
          ? this.draftRange.timeFrom
          : this.draftRange.timeTo;

      if (currentValue === value) return;

      this.draftRange = {
        timeFrom: field === 'timeFrom' ? value : this.draftRange.timeFrom,
        timeTo: field === 'timeTo' ? value : this.draftRange.timeTo,
      };
    },
    apply() {
      if (this.isLoading) {
        new Toast({
          message: this.$t('data_loading_please_wait') || 'Data is currently loading. Please wait...',
          type: 'warning',
          duration: 3000,
        });
        return;
      }

      if (this.draftTimeType !== 'custom_time') {
        if (this.filterSelected.timeType !== this.draftTimeType) {
          this.$store.commit({ type: 'updateFilter', field: 'timeType', value: this.draftTimeType });
          this.$emit('input', 'timeType', this.draftTimeType);
        }
        this.dialogOpen = false;
        return;
      }

      if (!this.draftRange.timeFrom || !this.draftRange.timeTo) return;

      const fromDate = startOfLocalDay(this.draftRange.timeFrom);
      const toDate = startOfLocalDay(this.draftRange.timeTo);
      if (fromDate > toDate) {
        new Toast({
          message: this.$t('date_range_invalid') || '"To" date must be on or after "From" date.',
          type: 'error',
          duration: 4000,
        });
        return;
      }

      if (this.isDateOutsideStatsBounds(fromDate) || this.isDateOutsideStatsBounds(toDate)) {
        new Toast({
          message: this.$t('date_outside_stats_range') || 'Selected dates are outside the available stats range.',
          type: 'error',
          duration: 4000,
        });
        return;
      }

      const newRange = {
        timeFrom: toLocalYmd(fromDate),
        timeTo: toLocalYmd(toDate),
      };

      const currentRange = this.filterSelected.time || {};
      const typeChanged = this.filterSelected.timeType !== 'custom_time';
      const datesChanged =
        currentRange.timeFrom !== newRange.timeFrom ||
        currentRange.timeTo !== newRange.timeTo;

      if (!datesChanged && !typeChanged) {
        new Toast({
          message: this.$t('dates_not_changed') || 'Dates have not changed. No reload needed.',
          type: 'default',
          duration: 2000,
        });
        this.dialogOpen = false;
        return;
      }

      // Store the range before timeType. A timeType update notifies the header
      // immediately, and that refresh was still reading the previous preset range.
      if (datesChanged) {
        this.$store.commit({ type: 'updateFilter', field: 'time', value: newRange });
      }
      if (typeChanged) {
        this.$store.commit({ type: 'updateFilter', field: 'timeType', value: 'custom_time' });
      }

      if (datesChanged) {
        this.$emit('input', 'time', newRange);
      } else {
        this.$emit('input', 'timeType', 'custom_time');
      }

      this.dialogOpen = false;
    },
  },
  computed: {
    ...mapGetters(['filterOptions', 'filterSelected', 'isLoading', 'isRTL']),
    presetTypes() {
      const types = this.filterOptions?.timeType || [];
      return types.filter(t => t !== 'custom_time');
    },
    canApply() {
      if (this.isLoading) return false;
      if (this.draftTimeType === 'custom_time') {
        return !!(this.draftRange.timeFrom && this.draftRange.timeTo);
      }
      return !!this.draftTimeType;
    },
    triggerLabel() {
      const tt = this.filterSelected.timeType;
      if (tt === 'custom_time') {
        const from = this.filterSelected.time?.timeFrom;
        const to = this.filterSelected.time?.timeTo;
        if (from && to) {
          try {
            // Parse YYYY-MM-DD as local calendar days (not UTC) so Central/other
            // negative-offset timezones don't show the previous day on the chip.
            return `${utilService.dateToDD_MM_YYYY(startOfLocalDay(from))} – ${utilService.dateToDD_MM_YYYY(startOfLocalDay(to))}`;
          } catch (e) {
            return this.$t('custom_time');
          }
        }
        return this.$t('custom_time');
      }
      if (tt) return this.$t(tt);
      return this.$t('select_time_range') || 'Select time range';
    },
    fields() {
      let dateRange = true;
      let viewBy = true;
      const { name, path } = this.$route;
      if (name === 'suggestion') viewBy = false;
      if (path.includes('suggestion')) dateRange = false;
      return { dateRange, viewBy };
    },
  },
  components: { Select, Option, DatePicker, Teleport },
};
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.time-filter-root {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  min-width: 0;
  position: relative;
}

.time-filter-container {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;

  .filter-select {
    @include filter-select;
    flex: 0 0 auto;
  }

  .view-by-select {
    flex: 0 0 auto;
  }
}

.time-filter-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 260px;
  min-height: 38px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #1e293b;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  outline: none;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &:focus-visible {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
  }

  &.time-filter-trigger-active {
    border-color: #3b82f6;
    background: #eff6ff;
    color: #1d4ed8;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }

  @media (max-width: 640px) {
    max-width: 200px;
    min-height: 36px;
    padding: 0 10px;
    font-size: 12px;
    gap: 6px;
  }
}

.time-filter-trigger-icon {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.85;
}

.time-filter-trigger-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: start;
}

.time-filter-trigger-chevron {
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.65;
  transition: transform 0.2s ease;

  .time-filter-trigger-active & {
    transform: rotate(180deg);
  }
}

.date-dialog-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 23, 42, 0.4);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  box-sizing: border-box;
  overflow-y: auto;
}

.date-dialog {
  width: min(460px, 100%);
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.8) inset,
    0 20px 50px rgba(15, 23, 42, 0.15),
    0 8px 20px rgba(15, 23, 42, 0.08);
  overflow: hidden;
  animation: date-dialog-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 500px) {
    width: 100%;
    max-width: calc(100vw - 32px);
  }
}

.date-dialog-rtl {
  direction: rtl;
}

@keyframes date-dialog-in {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.date-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  background: #fff;
  border-bottom: 1px solid #f1f5f9;
}

.date-dialog-title {
  flex: 1;
  min-width: 0;

  h3 {
    margin: 0;
    color: #0f172a;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
  }
}

.date-dialog-close {
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  svg {
    width: 20px;
    height: 20px;
  }

  path {
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }

  &:active {
    background: #e2e8f0;
  }
}

.date-dialog-body {
  display: flex;
  flex-direction: column;
}

.time-presets {
  list-style: none;
  margin: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.time-preset {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #334155;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  text-align: start;
  transition: background 0.15s ease, color 0.15s ease;
  outline: none;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }

  &:focus-visible {
    background: #f1f5f9;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.4) inset;
  }
}

.time-preset-active {
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;

  &:hover {
    background: #dbeafe;
    color: #1d4ed8;
  }
}

.time-preset-custom {
  border-top: 1px solid #f1f5f9;
  margin-top: 4px;
  padding-top: 14px;
  border-radius: 0 0 8px 8px;
}

.time-preset-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.time-preset-check {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.date-dialog-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 16px 20px 20px;
  background: #fafbfc;
  border-top: 1px solid #f1f5f9;
  animation: fields-in 0.18s ease-out;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 14px 16px 16px;
  }
}

@keyframes fields-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.date-field-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 12px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
}

.date-field-label {
  display: block;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.date-picker-dialog.md-datepicker {
  width: 100%;
  min-height: 42px;
  margin: 0 !important;
  padding: 0 !important;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #94a3b8;
    background: #fafbfc;
  }

  &:focus-within {
    border-color: #3b82f6;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  /* Show calendar icon in modal (globally hidden in vendor.scss) */
  .md-date-icon {
    display: flex !important;
  }

  ::v-deep .md-field {
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    width: 100%;
    min-width: 0;
    min-height: 42px;
    margin: 0;
    padding: 0 10px;

    &::before,
    &::after {
      display: none !important;
    }

    .md-input {
      width: 100% !important;
      min-width: 0 !important;
      height: 40px;
      max-height: none !important;
      padding: 0 6px !important;
      color: #1e293b;
      font-size: 14px !important;
      font-weight: 500;
      text-align: start;
      letter-spacing: 0;
      line-height: 40px;
      cursor: pointer;

      &::placeholder {
        color: #94a3b8;
        font-weight: 400;
      }
    }

    .md-date-icon {
      flex: 0 0 18px;
      width: 18px;
      min-width: 18px;
      height: 18px;
      margin: 0;
      margin-inline-end: 6px;
      color: #3b82f6;
      opacity: 1;
      cursor: pointer;
      transition: transform 0.15s ease;

      &:hover {
        transform: scale(1.1);
      }

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }
}

.date-dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fff;

  @media (max-width: 480px) {
    padding: 14px 16px;
  }
}

.date-dialog-cancel,
.date-dialog-apply {
  min-height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  outline: none;

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  @media (max-width: 480px) {
    flex: 1;
    padding: 0 16px;
  }
}

.date-dialog-cancel {
  border: 1.5px solid #e2e8f0;
  background: #fff;
  color: #64748b;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
    color: #475569;
  }
}

.date-dialog-apply {
  border: 1.5px solid #3b82f6;
  background: #3b82f6;
  color: #fff;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.25);

  &:hover:not(:disabled) {
    background: #2563eb;
    border-color: #2563eb;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;
  }
}
</style>

<style lang="scss">
.md-datepicker-dialog {
  z-index: 10020 !important;
}

.md-datepicker-dialog.md-theme-default,
.md-datepicker-overlay {
  z-index: 10020 !important;
}

.md-menu-content {
  z-index: 10030 !important;
}
</style>
